"use client"

import { useState, useEffect } from "react"
import { Plus, Trash2, Edit2, Loader2, Database, Upload, FileText, ImageIcon, EyeOff, Eye } from "lucide-react"
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "@/lib/firebase"
import { toast } from "sonner"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import MediaLibrary from "./media-library"
import { ensureUniqueSlug } from "@/lib/slugs"

export default function MagazinesManager() {
  const [magazines, setMagazines] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [viewState, setViewState] = useState<"list" | "edit">("list")
  const [editingMagazine, setEditingMagazine] = useState<any | null>(null)

  // Form State
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [month, setMonth] = useState("")
  const [year, setYear] = useState(new Date().getFullYear().toString())
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pdfName, setPdfName] = useState("")
  const [portal, setPortal] = useState("all")
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [mediaTarget, setMediaTarget] = useState<"image" | "pdf">("image")

  const fetchMagazines = async () => {
    setIsLoading(true)
    try {
      const q = query(collection(db, "magazines"), orderBy("createdAt", "desc"))
      const querySnapshot = await getDocs(q)
      setMagazines(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    } catch (error) {
      console.error(error)
      toast.error("Failed to load magazines")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchMagazines()
  }, [])

  const resetForm = () => {
    setTitle("")
    setSlug("")
    setMonth("")
    setYear(new Date().getFullYear().toString())
    setImageFile(null)
    setImagePreview(null)
    setPdfFile(null)
    setPdfName("")
    setPortal("all")
    setEditingMagazine(null)
    setViewState("list")
  }

  const handleEdit = (mag: any) => {
    setEditingMagazine(mag)
    setTitle(mag.title || "")
    setSlug(mag.slug || "")
    setMonth(mag.month || "")
    setYear(mag.year || "")
    setImagePreview(mag.imageUrl || null)
    setPdfName(mag.pdfUrl ? "Existing PDF Linked" : "")
    setPortal(mag.portal || "all")
    setViewState("edit")
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this magazine?")) return
    try {
      await deleteDoc(doc(db, "magazines", id))
      toast.success("Magazine deleted")
      fetchMagazines()
    } catch (error) {
      toast.error("Failed to delete")
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      let imageUrl = imagePreview || ""
      if (imageFile) {
        const imgRef = ref(storage, `magazines/covers/${Date.now()}_${imageFile.name}`)
        await uploadBytes(imgRef, imageFile)
        imageUrl = await getDownloadURL(imgRef)
        
        await addDoc(collection(db, "media"), {
           title: `${title} Magazine Cover`,
           url: imageUrl,
           type: "image",
           folder: "images",
           createdAt: serverTimestamp(),
        })
      }

      let pdfUrl = editingMagazine?.pdfUrl || ""
      if (pdfFile) {
        // If the file object has a 'url' property, it was selected from the library
        if ((pdfFile as any).url) {
          pdfUrl = (pdfFile as any).url
        } else {
          const pdfRef = ref(storage, `magazines/files/${Date.now()}_${pdfFile.name}`)
          await uploadBytes(pdfRef, pdfFile)
          pdfUrl = await getDownloadURL(pdfRef)
          
          await addDoc(collection(db, "media"), {
             title: `${title} Magazine PDF`,
             url: pdfUrl,
             type: "document",
             folder: "documents",
             createdAt: serverTimestamp(),
          })
        }
      }

      // Generate or validate slug
      const finalSlug = await ensureUniqueSlug("magazines", slug || title, editingMagazine?.id || undefined)

      const magData = {
        title: title || "",
        slug: finalSlug,
        month: month || "",
        year: year || "",
        imageUrl: imageUrl || "",
        pdfUrl: pdfUrl || "",
        portal: portal || "all",
        updatedAt: serverTimestamp(),
        createdAt: editingMagazine?.createdAt || serverTimestamp(),
        isVisible: true
      }

      if (editingMagazine) {
        const existing = magazines.find(m => m.id === editingMagazine.id)
        await updateDoc(doc(db, "magazines", editingMagazine.id), {
          ...magData,
          isVisible: existing?.isVisible !== undefined ? existing.isVisible : true
        })
        toast.success("Magazine updated successfully")
      } else {
        await addDoc(collection(db, "magazines"), magData)
        toast.success("Magazine published successfully")
      }

      resetForm()
      fetchMagazines()
    } catch (error) {
      console.error(error)
      toast.error("Process failed")
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleVisibility = async (mag: any) => {
    try {
      const newStatus = !mag.isVisible
      await updateDoc(doc(db, "magazines", mag.id), { isVisible: newStatus })
      toast.success(newStatus ? "Status: Live" : "Status: Hidden")
      setMagazines(prev => prev.map(m => m.id === mag.id ? { ...m, isVisible: newStatus } : m))
    } catch (error) {
      toast.error("Failed to update visibility")
    }
  }

  if (viewState === "list") {
    return (
      <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
        <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">Magazine Archives</h3>
          <button onClick={() => setViewState("edit")} className="bg-slate-950 text-white px-4 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] transition-colors flex items-center gap-2">
            <Plus size={14} /> Publish Issue
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Issue</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Portal</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-8 py-12 text-center">
                    <Loader2 className="animate-spin inline-block text-[#b48c36]" />
                  </td>
                </tr>
              ) : magazines.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-8 py-12 text-center text-slate-400 text-sm">No issues found.</td>
                </tr>
              ) : (
                magazines.map((mag) => (
                  <tr key={mag.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-14 bg-slate-100 rounded overflow-hidden flex-shrink-0 border border-slate-200">
                          {mag.imageUrl && <img src={mag.imageUrl} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div>
                          <p className="font-serif font-bold text-slate-950 leading-none mb-1">{mag.title}</p>
                          <p className="text-[10px] text-slate-400 font-mono">/{mag.slug || mag.id}</p>
                          <p className="text-[9px] font-bold text-[#b48c36] uppercase tracking-widest mt-1">{mag.month} {mag.year}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${mag.portal === 'all' ? 'text-blue-500' : 'text-purple-500'}`}>
                        {(!mag.portal || mag.portal === 'all') ? 'Universal' : mag.portal.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-8 py-5">
                      <button 
                        onClick={() => toggleVisibility(mag)}
                        className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${mag.isVisible !== false ? 'text-emerald-500' : 'text-slate-400'}`}
                      >
                        {mag.isVisible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
                        {mag.isVisible !== false ? 'Live' : 'Hidden'}
                      </button>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(mag)} className="p-2 hover:bg-[#b48c36]/10 text-slate-400 hover:text-[#b48c36] transition-colors rounded">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(mag.id)} className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors rounded">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
      <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">
          {editingMagazine ? "Modify Issue" : "Publish New Issue"}
        </h3>
        <button onClick={resetForm} className="text-slate-400 hover:text-slate-950 transition-colors">
          Cancel
        </button>
      </div>

      <form onSubmit={handleSave} className="p-4 sm:p-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Magazine Title</label>
              <input 
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="e.g. Current Affairs Monthly"
                className="w-full bg-slate-50 border border-slate-100 p-4 font-serif text-lg focus:outline-none focus:border-[#b48c36]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">URL Identifier (Slug)</label>
              <input 
                type="text" 
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. current-affairs-jan-2024"
                className="w-full bg-slate-50 border border-slate-100 p-4 font-mono text-sm focus:outline-none focus:border-[#b48c36]"
              />
              <p className="text-[9px] text-slate-400 italic">Leave blank to auto-generate from title.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Month</label>
                <select value={month} onChange={(e) => setMonth(e.target.value)} required className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest">
                  <option value="">Select Month</option>
                  {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Year</label>
                <input type="text" value={year} onChange={(e) => setYear(e.target.value)} required className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest" />
              </div>
            </div>

            <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Portal Access</label>
                <select value={portal} onChange={(e) => setPortal(e.target.value)} className="w-full bg-slate-50 border border-slate-100 p-4 text-[10px] font-bold uppercase tracking-widest">
                  <option value="all">Universal</option>
                  <option value="tspsc">TSPSC Only</option>
                  <option value="appsc">APPSC Only</option>
                </select>
            </div>
          </div>

          <div className="space-y-8">
            <div className="space-y-4">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cover Artwork</label>
              <div className="flex gap-4 items-start">
                <div className="w-24 h-32 bg-slate-50 border border-slate-100 rounded flex items-center justify-center overflow-hidden shrink-0">
                  {imagePreview ? <img src={imagePreview} className="w-full h-full object-cover" alt="" /> : <ImageIcon className="text-slate-200" size={32} />}
                </div>
                <div className="flex-1 space-y-2">
                   <label className="block w-full bg-slate-950 text-white p-3 text-[10px] font-bold uppercase tracking-widest text-center cursor-pointer hover:bg-[#b48c36] transition-colors">
                      Upload New Cover
                      <input type="file" onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
                      }} className="hidden" accept="image/*" />
                   </label>
                   <button type="button" onClick={() => { setMediaTarget("image"); setIsMediaLibraryOpen(true); }} className="w-full border border-slate-200 p-3 text-[10px] font-bold uppercase tracking-widest hover:bg-slate-50 flex items-center justify-center gap-2">
                      <Database size={14} /> Browse Library
                   </button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Magazine PDF File</label>
              <div className="p-6 border-2 border-dashed border-slate-100 flex flex-col items-center gap-4 text-center">
                <FileText size={40} className={pdfFile || pdfName ? "text-emerald-500" : "text-slate-100"} />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-950">{pdfFile ? pdfFile.name : (pdfName || "No PDF Linked")}</p>
                  <p className="text-[9px] text-slate-400 mt-1">High-quality document optimized for reading.</p>
                </div>
                <div className="flex gap-2 w-full mt-2">
                  <label className="flex-1 bg-white border border-slate-900 text-slate-900 p-3 text-[10px] font-bold uppercase tracking-widest text-center cursor-pointer hover:bg-slate-50 transition-colors">
                    Upload PDF
                    <input type="file" onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) { setPdfFile(file); setPdfName(file.name); }
                    }} className="hidden" accept="application/pdf" />
                  </label>
                  <button type="button" onClick={() => { setMediaTarget("pdf"); setIsMediaLibraryOpen(true); }} className="flex-1 border border-slate-200 p-3 text-[10px] font-bold uppercase tracking-widest hover:bg-slate-50 flex items-center justify-center gap-2">
                    <Database size={14} /> Library
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-100 flex justify-end gap-4">
          <button type="button" onClick={resetForm} className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-950">
            Discard
          </button>
          <button type="submit" disabled={isSubmitting} className="bg-slate-950 text-white px-12 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] disabled:opacity-50 flex items-center gap-3">
            {isSubmitting ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
            {editingMagazine ? "Update Issue" : "Publish Issue"}
          </button>
        </div>
      </form>

      {/* Media Library Dialog */}
      <Dialog open={isMediaLibraryOpen} onOpenChange={setIsMediaLibraryOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-6 bg-slate-950 text-white">
            <DialogTitle className="font-serif text-xl">Select Assets</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            <MediaLibrary 
              allowSelection 
              initialTab={mediaTarget === "image" ? "images" : "documents"}
              onSelect={(media) => {
                if (mediaTarget === "image") {
                  setImagePreview(media.url)
                  setImageFile(null)
                } else {
                  setPdfName(media.title || "Selected from Library")
                  setPdfFile(null)
                  // We need to store the selected URL back into our state
                  if (editingMagazine) setEditingMagazine({...editingMagazine, pdfUrl: media.url})
                  else setPdfFile({ name: media.title, type: "selected_from_library", url: media.url } as any)
                }
                setIsMediaLibraryOpen(false)
              }} 
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
